import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { CaseContextService } from '../../core/services/case-context.service';
import { CaseApiService } from '../../core/services/case-api.service';
import { CaseModalService } from '../../features/broker/cases/case-modal.service';
import { CaseDiffService } from '../../core/services/case-diff.service';
import { Case, INTENT_LABELS } from '../../core/services/case.model';

@Component({
  selector: 'app-case-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './case-bar.component.html',
  styleUrl: './case-bar.component.scss',
})
export class CaseBarComponent {
  caseCtx = inject(CaseContextService);
  private api = inject(CaseApiService);
  private modal = inject(CaseModalService);
  private diffService = inject(CaseDiffService); // ensure singleton instantiated
  private destroyRef = inject(DestroyRef);

  /** Now lives on CaseContextService so the topbar pill can toggle the
   *  same picker. We expose it as a getter so the template still reads
   *  pickerOpen() the same way. */
  get pickerOpen() { return this.caseCtx.pickerOpen; }
  recent = signal<Case[]>([]);

  readonly intentLabels = INTENT_LABELS;

  readonly summary = computed(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return '';
    const bits: string[] = [];
    if (c.age != null) bits.push(`${c.age}`);
    if (c.state) bits.push(c.state);
    if (c.premium != null) bits.push(`$${(c.premium / 1000).toFixed(0)}K`);
    if (c.termMin != null && c.termMax != null) bits.push(`${c.termMin}–${c.termMax}yr`);
    if (c.intents.length) bits.push(c.intents.map(i => this.intentLabels[i]).join(' · '));
    return bits.join(' · ');
  });

  readonly relativeOpened = computed(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return '';
    const ms = Date.now() - new Date(c.lastOpenedAt + 'Z').getTime();
    const days = Math.floor(ms / 86400000);
    if (days < 1) return 'opened today';
    if (days === 1) return 'opened yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  });

  openPicker() {
    if (!this.pickerOpen()) this.loadRecent();
    this.caseCtx.togglePicker();
  }

  closePicker() { this.caseCtx.closePicker(); }

  loadRecent() {
    this.api.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => this.recent.set(list.slice(0, 5)));
  }

  selectCase(c: Case) {
    // CaseDiffService handles touch (with real fit IDs) when setActive fires
    this.caseCtx.setActive(c);
    this.closePicker();
  }

  newCase() {
    this.closePicker();
    this.modal.open(null).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(input => {
      if (!input) return;
      this.api.create(input).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ id }) => {
        this.api.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
          const fresh = list.find(c => c.id === id);
          if (fresh) this.caseCtx.setActive(fresh);
        });
      });
    });
  }

  editActive() {
    const active = this.caseCtx.activeCase();
    if (!active) return;
    this.modal.open(active).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(input => {
      if (!input) return;
      this.api.update(active.id, input).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.api.list().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
          const fresh = list.find(c => c.id === active.id);
          if (fresh) this.caseCtx.setActive(fresh);
        });
      });
    });
  }

  clearActive() {
    this.caseCtx.clear();
    this.closePicker();
  }
}
